import { orderBy } from 'lodash';

import { AllThreadsAndMessages } from '@missionlabs/smartagent-service-thread';
import { SAMessageType } from '@missionlabs/smartagent-chat-components-lib/dist/ChatMessage/chat-message.types';
import { SocialChatJsonMessage } from '@missionlabs/smartagent-service-chat/dist/types/socialChatJsonMessage';

import ContactState from 'store/contact/contact.state';
import { getEmailFromContactAttributes } from 'views/Metrics/HistoricMetrics/ContactHistory/utils';
import { getChannelType } from 'views/Metrics/HistoricMetrics/utils';
import { paramsToQueryString, IStrObj } from 'utils';
import { getThreads } from 'services/api/api.thread';

import { ChannelType, IInteraction } from 'views/Metrics/HistoricMetrics/types';
import { Sort } from './contactHistory.state';
import { socialChannels, voiceChannels, webchatChannels, taskChannels } from './contactHistory.constants';

type SortMap = {
    [key in Sort]: { direction: 'asc' | 'desc'; field: string };
};

type MessageContent = string | SAMessageType | SocialChatJsonMessage;

export const JSONParse = (string: string): [Error | undefined, any] => {
    try {
        const parsedContent = JSON.parse(string);
        return [undefined, parsedContent];
    } catch (error) {
        return [error as Error, undefined];
    }
};

export const transformMessageContent = (content: string, language: string = 'en'): MessageContent => {
    let transformedContent: MessageContent;

    const [error, parsedContent]: [Error | undefined, any] = JSONParse(content);

    // Narrow down to a string if error occurs
    if (error) {
        transformedContent = content;
    }
    // Attempt to grab the message content using the language given
    else if (parsedContent[language]) {
        transformedContent = parsedContent[language] as SAMessageType;
    } else {
        transformedContent = parsedContent as SocialChatJsonMessage;
    }

    return transformedContent;
};

export const constructQueryStringByChannel = (contact: ContactState, channelType: ChannelType): string => {
    // Build up the query parameters based on the channel type given. This will need extending as we
    // expand the Contact History feature to other channels
    let queryParameters: IStrObj = {};

    if (socialChannels.includes(channelType)) {
        // Mapping Contact History channels to corresponding value for channel query parameter
        const socialChannelMapping: IStrObj = {
            'TWITTER TWEET': 'Twitter Tweets',
            'TWITTER DM': 'Twitter DM',
            'FACEBOOK MESSENGER': 'Facebook DM',
            'FACEBOOK POST': 'Facebook Comments',
            'INSTAGRAM IG-DM': 'Instagram DM',
            'INSTAGRAM IG-COMMENT': 'Instagram Comments',
            'WHATSAPP WHATSAPP-DM': 'WhatsApp DM',
            'SMS SMS-DM': 'SMS DM'
        };

        const useSocialHandle = channelType === 'WHATSAPP WHATSAPP-DM' || channelType ===  'SMS SMS-DM';
        const isChannelWithOutboundContacts = channelType === 'SMS SMS-DM';
        queryParameters = {
            channel: socialChannelMapping[channelType],
            ...(useSocialHandle && {
                socialHandle: contact.attributes["sa-customer-endpoint-address"],
            }),
            ...(!useSocialHandle && {
                uniqueSocialId: contact.attributes["sa-customer-endpoint-id"],
            }),
            ...(!isChannelWithOutboundContacts && { answered: "true" }),
        }
    }
    else if (voiceChannels.includes(channelType)) {
        const { customerEndpointAddress: phonenumberE164, channel } = contact;
        if (phonenumberE164 !== 'anonymous') {
            queryParameters = { answered: 'true', phonenumberE164, channel };
        }
    } else if (webchatChannels.includes(channelType)) {
        const chatEmail = getEmailFromContactAttributes(contact.attributes);
        if (chatEmail) {
            const { channel } = contact;
            queryParameters = { answered: 'true', channel, chatEmail };
        }
    } else if (taskChannels.includes(channelType)) {
        const { channel } = contact;
        const taskEmail = contact.attributes['sa-customer-endpoint-address'];
        if (taskEmail) {
            queryParameters = { channel, taskEmail };
        }
    } else {
        queryParameters = { answered: 'true', contactID: contact.ID };
    }

    return paramsToQueryString(queryParameters);
};

export const sortContactHistoryInteractions = (interactions: IInteraction[], sort: Sort): IInteraction[] => {
    // Map given sort to a sort direction and field to sort by
    const sortMap: SortMap = {
        'date-time-asc': {
            direction: 'asc',
            field: 'connectedToAgentTimestamp',
        },
        'date-time-desc': {
            direction: 'desc',
            field: 'connectedToAgentTimestamp',
        },
        'agent-name-asc': {
            direction: 'asc',
            field: 'agentName',
        },
        'agent-name-desc': {
            direction: 'desc',
            field: 'agentName',
        },
        'queue-asc': {
            direction: 'asc',
            field: 'queueName',
        },
        'queue-desc': {
            direction: 'desc',
            field: 'queueName',
        },
        'duration-asc': {
            direction: 'asc',
            field: 'interactionDuration',
        },
        'duration-desc': {
            direction: 'desc',
            field: 'interactionDuration',
        },
        'acw-duration-asc': {
            direction: 'asc',
            field: 'afterContactWorkDuration',
        },
        'acw-duration-desc': {
            direction: 'desc',
            field: 'afterContactWorkDuration',
        },
    };

    const { field, direction } = sortMap[sort];
    return orderBy(interactions, field, direction);
};

export const transformContacts = (contacts: ContactState[]) => {
    // Function to perform any needed transformations on the incoming interactions before being added to the contactHistory state
    return contacts.map(contact => {
		const { disconnectTimestamp, connectedToAgentTimestamp, attributes = {} } = contact;
        const interactionDuration = (disconnectTimestamp && connectedToAgentTimestamp) ? disconnectTimestamp - connectedToAgentTimestamp : 0;
        const channelType = getChannelType(contact);
        const agentName = contact.agentName ?? contact.attributes['sa-agent-name'];
        
        return { ...contact, interactionDuration, channelType, attributes, agentName } as IInteraction;
    });
};

export const getThreadMessageGroups = async (contacts: ContactState[]) => {
    const threadIds = [...new Set(contacts.map(contact =>  contact.attributes['sa-parent-thread-id'] || contact.attributes['sa-threadID']))];
    const threadMessageGroups = await getThreads(threadIds);    
    return threadMessageGroups;
};

export const getContactHistoryIdsFromOtherParties = (threadMessageGroups: AllThreadsAndMessages[], contacts: ContactState[]) => {
    const otherPartiesContactId: string[] = threadMessageGroups.flatMap((messageGroup) => {
        return messageGroup.messages.flatMap((message) => {
            if(message.contactID) {
                return contacts.find(contact => contact.ID === message.contactID) ? [] : [message.contactID];
            }
            return [];
        });
    });
    return [...new Set(otherPartiesContactId)];
};
